import { GALLERY_VALID_COMBINATIONS, PHOTO_SIZES } from '@spa/shared/constants/photos';

export const findPhotoById = (photoId, photos) => photos.find(({ id }) => id === photoId);

export const photoUrl = ({ hash, size = PHOTO_SIZES.SMALL }) =>
  `${process.env.CDN_PHOTOS_URL}/property-photos/${hash}/${size}.jpg`;

export const mapPhotos = (units, propertyPhotos, mainPhoto) => {
  const unitPhotos = getUnitPhotos(units, propertyPhotos, {}, true);
  const unassignedPhotos = propertyPhotos.reduce(
    (acc, { id, hash, placeholder }) =>
      !placeholder && !unitPhotos.some(photo => photo.id === id)
        ? [
            ...acc,
            { id, hash, href: photoUrl({ hash, size: PHOTO_SIZES.X_LARGE }), unitType: 'other', isPlaceholder: false }
          ]
        : acc,
    []
  );

  const mainPhotoUnitType = [...unitPhotos, ...unassignedPhotos].find(({ id }) => id === mainPhoto.id)?.unitType;

  return [...unitPhotos, ...unassignedPhotos]
    .sort((a, b) => (a.unitType === mainPhotoUnitType ? -1 : b.unitType === mainPhotoUnitType ? 1 : 0))
    .sort((a, b) => (a.id === mainPhoto.id ? -1 : b.id === mainPhoto.id ? 1 : 0));
};

const getUnitPhotos = (units, propertyPhotos, unitTypeCounters = {}, mainUnits = false) => {
  return units
    .reduce((acc, unit) => {
      const { type_code: typeCode, photos } = unit;
      const counter = (unitTypeCounters[typeCode] || 0) + 1;
      unitTypeCounters[typeCode] = counter;

      const unitPhotos = (photos || []).reduce((acc, { id, displayable }) => {
        const { hash, placeholder } = (displayable && findPhotoById(id, propertyPhotos)) || {};

        return hash
          ? [
              ...acc,
              {
                id,
                hash,
                href: photoUrl({ hash, size: PHOTO_SIZES.X_LARGE }),
                unitType: typeCode,
                unitIndex: counter > 1 ? counter : null,
                isPlaceholder: placeholder
              }
            ]
          : acc;
      }, []);

      const subunitPhotos = unit.subunits ? getUnitPhotos(unit.subunits, propertyPhotos, unitTypeCounters) : [];

      return [...acc, ...unitPhotos, ...subunitPhotos];
    }, [])
    .map(photo => ({
      ...photo,
      unitIndex: !photo.unitIndex && unitTypeCounters[photo.unitType] > 1 ? 1 : photo.unitIndex
    }));
};

export const groupPhotosByUnit = photos => {
  if (!photos || !Array.isArray(photos)) {
    return {};
  }

  return photos.reduce((acc, photo) => {
    const { unitType, unitIndex } = photo;
    const key = `${unitType}${unitIndex != null && unitType !== 'other' ? `:${unitIndex}` : ''}`;

    return { ...acc, [key]: [...(acc[key] || []), { ...photo }] };
  }, {});
};

export const getPhotosOrientation = photos =>
  Promise.all(photos.map(async photo => ({ ...photo, orientation: await getPhotoOrientation(photo.href) })));

export const groupGridPhotos = photos => {
  const photoGroups = [];
  let i = 0;

  if (shouldHadleFirstHorizontalPhoto(photos)) {
    photoGroups.push({ group_type: getGroupType([photos[0]]), photos: [photos[0]] });
    i = 1;
  }

  while (i < photos.length) {
    const groupOfThree = photos.slice(i, i + 3);
    if (shouldHandleLastVerticalPhoto(photos, i, groupOfThree)) {
      handleLastVerticalPhoto(photoGroups, groupOfThree[0]);
      i += 1;

      continue;
    }

    if (isValidGroupOfThree(groupOfThree)) {
      photoGroups.push({ group_type: getGroupType(groupOfThree), photos: groupOfThree });
      i += 3;

      continue;
    }

    const groupOfTwo = photos.slice(i, i + 2);
    if (isValidGroupOfTwo(photoGroups, groupOfTwo)) {
      photoGroups.push({ group_type: getGroupType(groupOfTwo), photos: groupOfTwo });
      i += 2;

      continue;
    }

    const groupOfOne = photos.slice(i, i + 1);
    if (isValidGroupOfOne(photos, groupOfOne)) {
      photoGroups.push({ group_type: getGroupType(groupOfOne), photos: groupOfOne });
    }

    i += 1;
  }

  return photoGroups;
};

const loadImage = src => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = src;
  });
};

const getPhotoOrientation = async href => {
  try {
    if (!href) {
      return 'horizontal';
    }

    const { width, height } = await loadImage(href);
    const aspectRatio = width / height;

    return aspectRatio > 1 ? 'horizontal' : 'vertical';
  } catch (error) {
    console.error(`Error loading image ${href}:`, error);

    return 'unknown';
  }
};

const isValidCombination = combination => GALLERY_VALID_COMBINATIONS.some(valid => valid.join('-') === combination);

const shouldHandleLastVerticalPhoto = (photos, index, groupOfThree) => {
  return (
    photos.length > 2 &&
    index === photos.length - 1 &&
    groupOfThree.length === 1 &&
    groupOfThree[0].orientation === 'vertical'
  );
};

const shouldHadleFirstHorizontalPhoto = photos => photos.length >= 3 && photos[0].orientation === 'horizontal';

const handleLastVerticalPhoto = (photoGroups, lastPhoto) => {
  const lastGroup = photoGroups[photoGroups.length - 1];
  const previousPhoto = lastGroup.photos.pop();
  lastGroup.group_type = getGroupType(lastGroup.photos);

  const newGroupPhotos = [previousPhoto, lastPhoto];
  photoGroups.push({ group_type: getGroupType(newGroupPhotos), photos: newGroupPhotos });
};

const isValidGroupOfThree = groupOfThree => isValidCombination(getGroupType(groupOfThree));

const isValidGroupOfTwo = (photoGroups, groupOfTwo) => {
  const twoCombination = getGroupType(groupOfTwo);
  const lastGroupType = photoGroups.length > 0 ? photoGroups[photoGroups.length - 1].group_type : '';

  return (
    !(lastGroupType === 'horizontal-horizontal' && twoCombination === 'horizontal-horizontal') &&
    isValidCombination(twoCombination)
  );
};

const isValidGroupOfOne = (photos, groupOfOne) =>
  photos.length > 1 && groupOfOne[0].orientation !== 'vertical' && isValidCombination(getGroupType(groupOfOne));

const getGroupType = group => group.map(photo => photo.orientation).join('-');
